/**
 * Gantt.
 *
 * Built against taskdojo-app/src/screens/GanttScreen.js -- the same bar
 * derivation, the same colour rules, the same filters and sorts.
 *
 * The one structural difference is how the thing scrolls. The phone keeps two
 * ScrollViews in step by hand, forwarding scroll offsets between the header and
 * the body; a browser has position:sticky, so the header row and the label
 * column pin themselves inside a single scroller and cannot drift out of step
 * because there is only one scroll position. That is a simplification, not a
 * shortcut.
 *
 * How a bar gets its extent, in the phone's order of preference:
 *
 *     start + end        -> exactly that
 *     start + deadline   -> start to deadline
 *     start only         -> a week from the start
 *     end only           -> the week before the end
 *     deadline only      -> the day before the deadline
 *     nothing at all     -> a zero-width mark at today
 *
 * A task with no dates is still shown, drawn dashed and pinned to today, rather
 * than hidden. The phone learnt that one: filtering them out meant tasks
 * silently vanished from the chart with nothing to say why.
 *
 * Not ported: dragging a bar to move or resize it, and pinch zoom. Both are
 * touch gestures that need a different design on a pointer device -- a drag
 * that rewrites a task's dates deserves its own thinking rather than a
 * transliteration, and it is the one interaction here that writes to the
 * database. Zoom is buttons for now.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useTheme } from '../../contexts/ThemeContext';
import { FilterPill, SectionTitle, raised, pressable } from '../dojo';
import { formatDateFull, formatShortDateTime } from '../../utils/datetime';
import type { Task, Habit } from '../../services/firebaseService';

const DAY_MS = 86400000;
const ROW_H = 44;
const BAR_H = 24;
const LABEL_W = 180;
const MIN_PX_PER_DAY = 6;
const MAX_PX_PER_DAY = 400;

type SortKey = 'manual' | 'start_date' | 'deadline';

interface GanttItem {
  id: string;
  name: string;
  start: Date;
  end: Date;
  deadline: Date | null;
  color: string;
  progress: number;
  type: 'task' | 'habit';
  status?: string;
  category?: string;
  tag?: string | null;
  dependencies: string[];
  isWatching: boolean;
  hasTimeframe: boolean;
}

const parseDate = (v: any): Date | null => {
  if (!v) return null;
  const d = typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Mobile's getTaskProgress, value for value. */
const taskProgress = (t: Task) => {
  if (t.progress != null && t.progress > 0) return t.progress / 100;
  switch (t.status) {
    case 'completed': return 1;
    case 'in_progress': return 0.5;
    case 'paused': return 0.25;
    default: return 0;
  }
};

const GanttChart: React.FC = () => {
  const { state, getTodayCompletions } = useApp();
  const { colors, isDark } = useTheme();

  const uid = state.user?.uid;
  const tasks = (state.tasks || []) as Task[];
  const habits = (state.habits || []) as Habit[];
  const doneToday = getTodayCompletions();

  const [pxPerDay, setPxPerDay] = useState(24);
  const [hideCompleted, setHideCompleted] = useState(false);
  const [showHabits, setShowHabits] = useState(false);
  const [showWatching, setShowWatching] = useState(true);
  const [sortBy, setSortBy] = useState<SortKey>('manual');
  const [selected, setSelected] = useState<string | null>(null);

  const scroller = useRef<HTMLDivElement>(null);
  const scrolledOnce = useRef(false);

  /* ------------------------------ the rows ------------------------------ */

  const items = useMemo<GanttItem[]>(() => {
    const out: GanttItem[] = [];

    tasks.forEach((t) => {
      const done = t.completed || t.status === 'completed';
      if (hideCompleted && done) return;
      // "Watched" here means assigned to somebody else, which is the phone's
      // definition on this screen -- not the watchers array.
      const watching = !!uid && !!t.assignedTo && t.assignedTo !== uid;
      if (!showWatching && watching) return;

      const startDT = parseDate(t.startDateTime);
      const endDT = parseDate(t.endDateTime);
      const startedAt = parseDate(t.startedAt);
      const deadline = parseDate(t.deadline);
      const start = startDT || startedAt;

      let a: Date;
      let b: Date;
      if (start && endDT) { a = start; b = endDT; }
      else if (start && deadline) { a = start; b = deadline; }
      else if (start) { a = start; b = new Date(start.getTime() + 7 * DAY_MS); }
      else if (endDT) { a = new Date(endDT.getTime() - 7 * DAY_MS); b = endDT; }
      else if (deadline) { a = new Date(deadline.getTime() - DAY_MS); b = deadline; }
      else { const now = new Date(); a = now; b = now; }

      // A backwards pair would otherwise produce a negative width.
      const lo = a <= b ? a : b;
      const hi = a <= b ? b : a;

      out.push({
        id: t.id, name: t.title, start: lo, end: hi, deadline,
        color: t.color || '#4A6FA5',
        progress: taskProgress(t),
        type: 'task', status: t.status, category: t.category, tag: t.tag,
        dependencies: t.dependencies || [],
        isWatching: watching,
        hasTimeframe: !!(startDT || endDT || startedAt || deadline),
      });
    });

    if (showHabits) {
      const now = new Date();
      // A habit is daily and has no end, so it is drawn from whenever it was
      // created, capped at thirty days back so one old habit cannot stretch
      // the whole chart.
      const lookback = new Date(now.getTime() - 30 * DAY_MS);
      habits.forEach((h) => {
        const completed = doneToday.includes(h.id);
        if (hideCompleted && completed) return;
        const created = parseDate(h.createdAt);
        out.push({
          id: h.id, name: h.title,
          start: created && created > lookback ? created : lookback,
          end: now, deadline: null,
          color: h.color || '#4A6FA5',
          progress: completed ? 1 : 0,
          type: 'habit', dependencies: [], isWatching: false, hasTimeframe: true,
        });
      });
    }

    // Tasks always sort above habits, whichever ordering is chosen.
    const byType = (x: GanttItem, y: GanttItem) =>
      x.type === y.type ? 0 : x.type === 'task' ? -1 : 1;

    const nullsLast = (x: Date | null, y: Date | null) => {
      if (!x && !y) return 0;
      if (!x) return 1;
      if (!y) return -1;
      return x.getTime() - y.getTime();
    };

    if (sortBy === 'start_date') {
      out.sort((x, y) => byType(x, y) || nullsLast(x.start, y.start));
    } else if (sortBy === 'deadline') {
      out.sort((x, y) => byType(x, y) || nullsLast(x.deadline || x.end, y.deadline || y.end));
    } else {
      out.sort((x, y) => byType(x, y) || x.start.getTime() - y.start.getTime());
    }

    return out;
  }, [tasks, habits, uid, hideCompleted, showHabits, showWatching, sortBy, doneToday]);

  /* ------------------------------ the range ------------------------------ */

  const { startDate, totalDays } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (items.length === 0) {
      const s = new Date(today);
      s.setDate(s.getDate() - 7);
      return { startDate: s, totalDays: 60 };
    }

    const earliest = new Date(Math.min(...items.map((i) => i.start.getTime())));
    let latest = new Date(Math.max(...items.map((i) => i.end.getTime())));
    items.forEach((i) => {
      if (i.deadline && i.deadline.getTime() > latest.getTime()) latest = new Date(i.deadline.getTime());
    });

    earliest.setDate(earliest.getDate() - 7);
    latest.setDate(latest.getDate() + 14);
    earliest.setHours(0, 0, 0, 0);
    latest.setHours(0, 0, 0, 0);

    const days = Math.max(Math.ceil((latest.getTime() - earliest.getTime()) / DAY_MS), 21);
    return { startDate: earliest, totalDays: days };
  }, [items]);

  const chartWidth = totalDays * pxPerDay;
  const xOf = (d: Date) => ((d.getTime() - startDate.getTime()) / DAY_MS) * pxPerDay;

  /* ----------------------------- the headers ----------------------------- */

  const months = useMemo(() => {
    const out: Array<{ label: string; left: number; width: number }> = [];
    let key = -1;
    let startPx = 0;
    for (let i = 0; i <= totalDays; i += 1) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      const k = d.getFullYear() * 12 + d.getMonth();
      if (k !== key) {
        if (out.length) out[out.length - 1].width = i * pxPerDay - startPx;
        out.push({
          // Full year, not '2-digit'. "SEP 26" reads as a date rather than a
          // month, and uppercasing it makes that worse.
          label: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          left: i * pxPerDay,
          width: 0,
        });
        startPx = i * pxPerDay;
        key = k;
      }
    }
    if (out.length) out[out.length - 1].width = (totalDays + 1) * pxPerDay - startPx;
    return out;
  }, [startDate, totalDays, pxPerDay]);

  const days = useMemo(() => {
    const todayStr = new Date().toDateString();
    const out: Array<{ d: Date; left: number; label: string; isToday: boolean; isWeekend: boolean }> = [];
    for (let i = 0; i < totalDays; i += 1) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      const dow = d.getDay();
      // Thin out the labels as the scale shrinks, so they never collide.
      let label = '';
      if (pxPerDay >= 30) label = String(d.getDate());
      else if (pxPerDay >= 12) label = (dow === 1 || d.getDate() === 1) ? String(d.getDate()) : '';
      else label = (d.getDate() === 1 || d.getDate() === 15) ? String(d.getDate()) : '';

      out.push({
        d, left: i * pxPerDay, label,
        isToday: d.toDateString() === todayStr,
        isWeekend: dow === 0 || dow === 6,
      });
    }
    return out;
  }, [startDate, totalDays, pxPerDay]);

  const nowX = xOf(new Date());

  /** Bring today into view once the chart has a width to scroll. */
  useEffect(() => {
    const el = scroller.current;
    if (!el || items.length === 0 || scrolledOnce.current) return;
    const view = el.clientWidth - LABEL_W;
    el.scrollLeft = Math.max(0, nowX - view / 3);
    scrolledOnce.current = true;
  }, [items.length, nowX]);

  const jumpToToday = () => {
    const el = scroller.current;
    if (!el) return;
    const view = el.clientWidth - LABEL_W;
    el.scrollTo({ left: Math.max(0, nowX - view / 3), behavior: 'smooth' });
  };

  const chosen = items.find((i) => i.id === selected) || null;

  const gridLine = isDark ? 'rgba(148,163,184,0.14)' : 'rgba(120,113,108,0.18)';
  const weekendWash = isDark ? 'rgba(148,163,184,0.05)' : 'rgba(120,113,108,0.06)';

  /* ------------------------------- render ------------------------------- */

  return (
    <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
      <div className="max-w-[1400px] mx-auto pt-4 pb-24">
        <div className="flex items-center gap-3 px-5 pt-2 pb-4">
          <h1 className={`text-[30px] sm:text-[34px] font-black tracking-[-0.9px] flex-1 ${colors.text}`}>
            Gantt
          </h1>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPxPerDay((p) => Math.max(MIN_PX_PER_DAY, p / 1.5))}
              aria-label="Zoom out"
              className={`w-9 h-9 rounded-[12px] flex items-center justify-center ${raised} ${pressable}
                ${colors.cardBorder} ${colors.card} ${colors.textSecondary}`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M5 12h14" strokeLinecap="round" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setPxPerDay((p) => Math.min(MAX_PX_PER_DAY, p * 1.5))}
              aria-label="Zoom in"
              className={`w-9 h-9 rounded-[12px] flex items-center justify-center ${raised} ${pressable}
                ${colors.cardBorder} ${colors.card} ${colors.textSecondary}`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M12 5v14M5 12h14" strokeLinecap="round" />
              </svg>
            </button>
            <button
              type="button"
              onClick={jumpToToday}
              className={`rounded-[12px] ${raised} ${pressable} ${colors.cardBorder} ${colors.card}
                ${colors.text} px-3 h-9 text-[13px] font-extrabold`}
            >
              Today
            </button>
          </div>
        </div>

        {/* Filters, in the phone's order. */}
        <div className="flex flex-wrap gap-2 px-5 pb-4 items-center">
          <FilterPill active={hideCompleted} onClick={() => setHideCompleted((v) => !v)}>
            Hide completed
          </FilterPill>
          <FilterPill active={showHabits} onClick={() => setShowHabits((v) => !v)}>
            Habits
          </FilterPill>
          <FilterPill active={showWatching} onClick={() => setShowWatching((v) => !v)}>
            Assigned to others
          </FilterPill>

          <label className="ml-auto flex items-center gap-2">
            <span className={`text-[12px] font-bold uppercase tracking-[0.5px] ${colors.textSecondary}`}>
              Sort
            </span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortKey)}
              className={`rounded-[12px] border-2 ${colors.cardBorder} ${colors.card} ${colors.text}
                px-3 py-1.5 text-[13px] font-bold focus:outline-none focus:border-dojo-red`}
            >
              <option value="manual">Start (grouped)</option>
              <option value="start_date">Start date</option>
              <option value="deadline">Deadline</option>
            </select>
          </label>
        </div>

        <div className="px-5">
          {items.length === 0 ? (
            <div className="flex flex-col items-center text-center py-20">
              <div className={`w-[88px] h-[88px] rounded-[30px] border-2 ${colors.cardBorder} ${colors.card}
                flex items-center justify-center mb-[18px]`}>
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" className={colors.textMuted}>
                  <path d="M4 6h10M4 12h16M4 18h7" strokeLinecap="round" />
                </svg>
              </div>
              <h2 className="text-xl font-black tracking-[-0.4px] mb-1.5">Nothing to plot</h2>
              <p className={`text-[15px] font-semibold ${colors.textSecondary}`}>
                {hideCompleted || !showWatching
                  ? 'No items match these filters.'
                  : 'Add a task and it appears here.'}
              </p>
            </div>
          ) : (
            <div
              ref={scroller}
              className={`overflow-x-auto rounded-[15px] border-2 ${colors.cardBorder} ${colors.card}`}
            >
              <div style={{ width: LABEL_W + chartWidth, position: 'relative' }}>
                {/* ------------------------------ header ------------------------------ */}
                <div
                  className={`sticky top-0 z-20 ${colors.card}`}
                  style={{ borderBottom: `2px solid ${gridLine}` }}
                >
                  {/* Months */}
                  <div className="relative" style={{ height: 26 }}>
                    <div
                      className={`absolute left-0 top-0 bottom-0 z-10 ${colors.card}`}
                      style={{ width: LABEL_W, borderRight: `2px solid ${gridLine}` }}
                    />
                    {months.map((m) => (
                      <div
                        key={m.label + m.left}
                        className={`absolute top-0 h-full flex items-center px-2 text-[11px] font-extrabold
                          tracking-[0.3px] ${colors.textSecondary}`}
                        style={{ left: LABEL_W + m.left, width: m.width, borderLeft: `1px solid ${gridLine}` }}
                      >
                        {m.width > 58 ? m.label : ''}
                      </div>
                    ))}
                  </div>

                  {/* Days */}
                  <div className="relative" style={{ height: 24 }}>
                    <div
                      className={`absolute left-0 top-0 bottom-0 z-10 ${colors.card} flex items-center px-2`}
                      style={{ width: LABEL_W, borderRight: `2px solid ${gridLine}` }}
                    >
                      <span className={`text-[11px] font-bold uppercase tracking-[0.5px] ${colors.textMuted}`}>
                        {items.length} item{items.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {days.map((d) => (
                      <div
                        key={d.left}
                        className="absolute top-0 h-full flex items-center justify-center"
                        style={{
                          left: LABEL_W + d.left,
                          width: pxPerDay,
                          backgroundColor: d.isWeekend ? weekendWash : undefined,
                        }}
                      >
                        <span
                          className="text-[10px] font-bold tabular-nums"
                          style={{ color: d.isToday ? '#E34234' : undefined }}
                        >
                          {d.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ------------------------------- rows ------------------------------- */}
                <div className="relative">
                  {/* Weekend washes and day rules, drawn once behind every row. */}
                  <div className="absolute inset-0 pointer-events-none" style={{ left: LABEL_W }}>
                    {days.map((d) => (
                      <div
                        key={d.left}
                        className="absolute top-0 bottom-0"
                        style={{
                          left: d.left,
                          width: pxPerDay,
                          backgroundColor: d.isWeekend ? weekendWash : undefined,
                          borderLeft: pxPerDay >= 12 ? `1px solid ${gridLine}` : undefined,
                        }}
                      />
                    ))}
                  </div>

                  {/* Now. */}
                  <div
                    className="absolute top-0 bottom-0 pointer-events-none z-10"
                    style={{ left: LABEL_W + nowX, width: 2, backgroundColor: '#E34234' }}
                  />

                  {items.map((item) => {
                    const done = item.status === 'completed';
                    const noTime = item.type === 'task' && !item.hasTimeframe;
                    const left = xOf(item.start);
                    // A dateless task collapses to zero width, which drew a
                    // 3px sliver nobody could see or click. One day wide is
                    // enough to read as the dashed "no dates" bar it is.
                    const width = Math.max(xOf(item.end) - left, noTime ? pxPerDay : 3);
                    const isSel = selected === item.id;

                    // The phone's colour rules, alpha suffixes and all.
                    const bg = noTime
                      ? `${item.color}20`
                      : item.type === 'habit'
                        ? (item.progress === 1 ? `${item.color}60` : `${item.color}25`)
                        : (done ? `${item.color}40` : `${item.color}60`);
                    const border = item.type === 'habit'
                      ? (item.progress === 1 ? `${item.color}AA` : `${item.color}60`)
                      : `${item.color}${done ? '80' : isSel ? 'DD' : 'AA'}`;
                    const dashed = noTime || (item.type === 'habit' && item.progress === 0);

                    return (
                      <div
                        key={item.id}
                        className="relative"
                        style={{ height: ROW_H, borderBottom: `1px solid ${gridLine}` }}
                      >
                        {/* Label, pinned while the timeline scrolls under it. */}
                        <div
                          className={`sticky left-0 z-10 h-full flex items-center gap-1.5 px-2.5 ${colors.card}`}
                          style={{ width: LABEL_W, borderRight: `2px solid ${gridLine}` }}
                        >
                          <span
                            className="w-1.5 h-[18px] rounded-sm flex-shrink-0"
                            style={{ backgroundColor: item.color }}
                          />
                          {item.type === 'habit' && <span className="text-[11px]">🔥</span>}
                          {item.isWatching && (
                            <span className={`text-[10px] ${colors.textMuted}`} title="Assigned to someone else">👁</span>
                          )}
                          <span className={`flex-1 min-w-0 text-[12.5px] font-bold truncate
                            ${done ? `line-through ${colors.textSecondary}` : ''}`}>
                            {item.name}
                          </span>
                        </div>

                        {/* Bar. */}
                        <button
                          type="button"
                          onClick={() => setSelected(isSel ? null : item.id)}
                          title={item.name}
                          className="absolute overflow-hidden"
                          style={{
                            left: LABEL_W + left,
                            width,
                            height: BAR_H,
                            top: (ROW_H - BAR_H) / 2,
                            borderRadius: 7,
                            backgroundColor: bg,
                            border: `1.5px ${dashed ? 'dashed' : 'solid'} ${border}`,
                            opacity: done ? 0.75 : 1,
                          }}
                        >
                          {item.type === 'task' && item.progress > 0 && (
                            <span
                              className="absolute left-0 top-0 bottom-0"
                              style={{
                                width: `${Math.min(item.progress * 100, 100)}%`,
                                backgroundColor: item.color,
                                opacity: done ? 0.5 : 0.85,
                              }}
                            />
                          )}
                        </button>

                        {/* Deadline, where it falls outside the bar's end. */}
                        {item.deadline && (
                          <span
                            className="absolute pointer-events-none"
                            style={{
                              left: LABEL_W + xOf(item.deadline) - 4,
                              top: ROW_H / 2 - 4,
                              width: 8, height: 8,
                              transform: 'rotate(45deg)',
                              backgroundColor: '#E34234',
                            }}
                            title={`Deadline ${formatDateFull(item.deadline)}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* What the colours mean. Without it the dashed bars in particular read
            as a rendering fault rather than "this task has no dates". */}
        {items.length > 0 && (
          <div className={`px-5 mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-[11.5px] font-semibold
            ${colors.textSecondary}`}>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-[2px] bg-dojo-red inline-block" /> now
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-dojo-red inline-block rotate-45" /> deadline
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-2.5 rounded-[3px] border border-dashed inline-block"
                style={{ borderColor: colors.textSecondary.includes('slate') ? '#94a3b8' : '#78716c' }} />
              no dates set — shown at today
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-2.5 rounded-[3px] inline-block" style={{ backgroundColor: '#4A6FA5' }} />
              progress
            </span>
          </div>
        )}

        {/* The selected row, spelled out. A bar can only say so much. */}
        {chosen && (
          <div className="px-5 mt-5">
            <SectionTitle
              className="mb-3"
              right={
                <button type="button" onClick={() => setSelected(null)} aria-label="Close"
                  className={colors.textSecondary}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" />
                  </svg>
                </button>
              }
            >
              {chosen.name}
            </SectionTitle>
            <div className={`rounded-[15px] border-2 ${colors.cardBorder} ${colors.card} p-4
              grid grid-cols-2 sm:grid-cols-4 gap-4`}>
              {[
                { k: 'Starts', v: chosen.hasTimeframe ? formatShortDateTime(chosen.start) : 'not set' },
                { k: 'Ends', v: chosen.hasTimeframe ? formatShortDateTime(chosen.end) : 'not set' },
                { k: 'Deadline', v: chosen.deadline ? formatDateFull(chosen.deadline) : '—' },
                { k: 'Progress', v: `${Math.round(chosen.progress * 100)}%` },
              ].map((f) => (
                <div key={f.k}>
                  <p className={`text-[11px] font-bold uppercase tracking-[0.5px] ${colors.textMuted}`}>{f.k}</p>
                  <p className="text-[13.5px] font-bold mt-0.5">{f.v}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GanttChart;
